//#region [IMPORT]

// === THIRD-PARTY ===
import React, { useContext, useEffect, useState } from 'react';

// === STYLE / MATERIAL UI ===
import { useTheme } from '@mui/material/styles';
import { Box } from '@mui/material';

// === COMPONENTS ===
import { Fields     as Field } from './components/Fields/index.js';
import { Thumbnails as Thumb } from './components/Thumbnails/index.js';
import { Image      as Image } from './components/Image/index.js';
import { ProgressBar } from './components/ProgressBar/ProgressBar';
import { TaskContext } from '../TaskContext/TaskContext.jsx';

// === HOOKS ===
import { useTaskCancel } from 'components/useTaskCancel';

// === API ===
import { on, removeListener, requestCurrentFieldFocus, returnTask, sendMessageData } from 'api/prokey/prokey.websocket';

//#endregion

//-----------------------------------------------------------------
export function TaskPanel( props ) {
  //-----------------------------------------------------------------

  //#region [VALUES]

  const theme       = useTheme();
  const cancel_data = useTaskCancel();

  const { clearImages } = useContext( TaskContext );

  const [ fields_info , setFieldsInfo ] = useState( {} );
  const [ images_info , setImagesInfo ] = useState( {} );
  const [ thumbs_info , setThumbsInfo ] = useState( {} );

  //#endregion

  //#region [STYLES]

  const _styles = {
    container: {
      display : 'grid' ,
      padding : '1rem' ,
      width   : `100%` ,
      height  : `calc( 100% - 1rem )`,
      zIndex  : '0'    ,
      gridTemplateColumns : 'repeat(12, 1fr)',
      gridGap             : '1rem',
      backgroundColor : theme.palette.background.secondary[theme.palette.mode],
    },
  }

  const CONTAINER = {
    sx: _styles.container,
  }

  //#endregion

  useEffect(() => {
    return () => {
      // make sure all curent images are cleared from cache before a new task is loaded
      clearImages();
    };
  }, []); // empty dependency = called only once at startup

  useEffect(() => {
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, []); // empty dependency = called only once at startup

  useEffect( () => {

    //-----------------------------------------------------------------
    function handleFieldsInfo( data ) {
      //-----------------------------------------------------------------
      setFieldsInfo( data );
    };

    //-----------------------------------------------------------------
    function handleImagesInfo( data ) {
      //-----------------------------------------------------------------
      setImagesInfo( data );
    };

    //-----------------------------------------------------------------
    function handleThumbsInfo( data ) {
      //-----------------------------------------------------------------
      setThumbsInfo( data );
    };

    on( 'PROKEY_FIELDS_VIEW', handleFieldsInfo );
    on( 'PROKEY_IMAGES_VIEW', handleImagesInfo );
    on( 'PROKEY_THUMBS_VIEW', handleThumbsInfo );

    sendMessageData( 'PROKEY_FIELDS_VIEW' );
    sendMessageData( 'PROKEY_IMAGES_VIEW' );
    sendMessageData( 'PROKEY_THUMBS_VIEW' );

    return () => {
      removeListener( 'PROKEY_FIELDS_VIEW', handleFieldsInfo );
      removeListener( 'PROKEY_IMAGES_VIEW', handleImagesInfo );
      removeListener( 'PROKEY_THUMBS_VIEW', handleThumbsInfo );
    };
  }, [] );

  useEffect( () => {
    if( fields_info.is_detached ) sendMessageData( 'TASK_VIEW_FIELDS_UNSUBSCRIBE' );
  }, [fields_info] );

  useEffect( () => {
    if( images_info.is_detached ) sendMessageData( 'TASK_VIEW_IMAGES_UNSUBSCRIBE' );
  }, [images_info] );

  useEffect( () => {
    if( thumbs_info.is_detached ) sendMessageData( 'TASK_VIEW_THUMBS_UNSUBSCRIBE' );
  }, [thumbs_info] );

  useEffect(() => {
    if( cancel_data.id == null ) return; 

    returnTask();
  }, [cancel_data]);

  //#region [EVENTS]

  //-----------------------------------------------------------------
  function onKeyDown(event) {
    //-----------------------------------------------------------------
    if (event.keyCode === 192) { // ` - grave accent key
      event.preventDefault();
      requestCurrentFieldFocus();
    }
  } 

  //#endregion

  //#region [DISPLAY]

  //-----------------------------------------------------------------
  function display() {
    //-----------------------------------------------------------------
    const is_fields_visible = fields_info.is_detached != undefined && fields_info.is_detached === false;
    const is_images_visible = images_info.is_detached != undefined && images_info.is_detached === false;
    const is_thumbs_visible = thumbs_info.is_detached != undefined && thumbs_info.is_detached === false;
    return (
      <>
        <Box { ...CONTAINER } >
          { is_fields_visible ? <Field fill={fields_info.fill} /> : null }
          { is_images_visible ? <Image fill={images_info.fill} /> : null }
          { is_thumbs_visible ? <Thumb fill={thumbs_info.fill} /> : null }
        </Box>
        <ProgressBar />
      </>
    )
  }
  //#endregion

  return display();
}